import { Skeleton } from "@design";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Cloud, Info } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getPlatformCostsOptions, type ServiceCost } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { BarRow, PageSection, StatRow } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatNumber, formatUsd } from "@/i18n/format";
import { AdminPage } from "../AdminPage";
import { CostTrend } from "./CostTrend";
import { DEFAULT_RANGE, type RangeDays, RangeSelect } from "./usageRange";

const SERVICE_LABELS: Record<string, "foundryModels" | "foundryTools" | "saas"> = {
  "Foundry Models": "foundryModels",
  "Foundry Tools": "foundryTools",
  SaaS: "saas",
};

/** Sum costs per (friendly) service name across resource groups, largest first. */
function useServiceTotals(services: ServiceCost[]) {
  const { t } = useTranslation("adminUsage");
  const totals = new Map<string, number>();
  for (const entry of services) {
    const raw = entry.serviceName.replace(/^Azure /, "");
    const key = SERVICE_LABELS[raw];
    const name = key ? t(`costs.services.${key}`) : raw;
    totals.set(name, (totals.get(name) ?? 0) + entry.costUsd);
  }
  return [...totals.entries()]
    .map(([name, cost]) => ({ name, cost }))
    .filter((s) => s.cost > 0)
    .sort((a, b) => b.cost - a.cost);
}

function Bars({ title, items }: Readonly<{ title: string; items: Array<{ name: string; cost: number }> }>) {
  const { t } = useTranslation("adminUsage");
  const max = Math.max(0, ...items.map((item) => item.cost));
  return (
    <PageSection title={title}>
      {items.length === 0 ? (
        <Text variant="body-sm" tone="tertiary">
          {t("costs.empty")}
        </Text>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((item) => (
            <BarRow
              key={item.name}
              label={item.name}
              value={formatUsd(item.cost)}
              fraction={max > 0 ? item.cost / max : 0}
            />
          ))}
        </div>
      )}
    </PageSection>
  );
}

/** /admin/usage/costs: actual Azure billing (Cost Management), with its own range. */
export function PlatformCostsPage() {
  const { t } = useTranslation("adminUsage");
  const [range, setRange] = useState<RangeDays>(DEFAULT_RANGE);
  const costs = useQuery({
    ...getPlatformCostsOptions({ query: { days: Number(range) } }),
    placeholderData: keepPreviousData,
  });
  const data = costs.data?.data;
  const services = useServiceTotals(data?.byService ?? []);
  const trend = data?.dailyTrend ?? [];
  const dailyAvg = trend.length > 0 ? trend.reduce((sum, day) => sum + day.costUsd, 0) / trend.length : 0;
  const environments = (data?.byResourceGroup ?? []).map((rg) => ({ name: rg.resourceGroup, cost: rg.costUsd }));

  const body = () => {
    if (costs.isPending) {
      return (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full" />
          <Text variant="caption" tone="tertiary">
            {t("costs.loading")}
          </Text>
        </div>
      );
    }
    if (costs.isError || !data?.configured) {
      return (
        <EmptyState
          icon={<Cloud size={32} />}
          title={t("costs.notConfigured")}
          body={data?.message || t("costs.notConfiguredBody")}
        />
      );
    }
    return (
      <>
        <StatRow
          stats={[
            { label: t("costs.stats.total"), value: formatUsd(data.totalCostUsd ?? 0), hint: t(`range.${range}`) },
            {
              label: t("costs.stats.dailyAverage"),
              value: formatUsd(dailyAvg),
              hint: t("costs.stats.projected", { amount: formatUsd(dailyAvg * 30) }),
            },
            { label: t("costs.stats.environments"), value: formatNumber(environments.length) },
          ]}
        />
        <CostTrend days={trend} />
        <Bars title={t("costs.byEnvironment")} items={environments} />
        <Bars title={t("costs.byService")} items={services} />
        <div className="flex items-center gap-2">
          <Info size={14} className="shrink-0 text-content-tertiary" aria-hidden />
          <Text as="span" variant="caption" tone="tertiary">
            {t("costs.footnote")}
          </Text>
        </div>
      </>
    );
  };

  return (
    <AdminPage title={t("costs.title")} subtitle={t("costs.subtitle")}>
      <RangeSelect label={t("range.costsLabel")} value={range} onChange={setRange} />
      {body()}
    </AdminPage>
  );
}
